import { MONTHS_IN_YEAR } from '../constants/common';

type EffectiveInterestRateParams = {
  loanAmount: number;
  arrangementFee: number;
  annualInterestRate: number;
  numberOfMonths: number;
};

const MAX_ITERATIONS = 100;
const RATE_INTERVAL_TOLERANCE = 1e-12;
const MAX_MONTHLY_RATE = 1; // 100% per month

/**
 * Present value of a straight-line amortization payment stream discounted at the
 * given monthly rate. This is the standardized representative-example schedule
 * (full repayment within `numberOfMonths`). Each month a fixed amount
 * of principal is repaid and interest is charged on the outstanding balance at
 * the nominal monthly rate:
 *
 *   payment_t = amortization + balance_(t-1) * nominalMonthlyRate
 *   presentValue = Σ(t=1..N) payment_t / (1 + monthlyRate)^t
 */
function getPresentValue(
  loanAmount: number,
  annualInterestRate: number,
  numberOfMonths: number,
  monthlyRate: number,
): number {
  const amortization = loanAmount / numberOfMonths;
  const nominalMonthlyRate = annualInterestRate / 100 / MONTHS_IN_YEAR;
  let presentValue = 0;

  for (let month = 1; month <= numberOfMonths; month++) {
    const balanceBeforePayment = loanAmount - amortization * (month - 1);
    const payment = amortization + balanceBeforePayment * nominalMonthlyRate;
    presentValue += payment / Math.pow(1 + monthlyRate, month);
  }

  return presentValue;
}

/**
 * Solves for the effective interest rate from the present-value equation:
 *
 *   N = Σ(t=1..numberOfMonths) payment_t / (1 + r/12)^t
 *
 * where `N` is the net amount received by the customer (loan amount minus the
 * arrangement fee) and `payment_t` are the monthly payments of a straight-line
 * amortization loan at the nominal interest rate, fully repaid over
 * `numberOfMonths`.
 *
 * The monthly rate `r/12` is found via bisection (the present value decreases
 * monotonically as the rate increases) and then annualized with monthly
 * compounding to yield the effective annual interest rate as a percentage.
 */
export function getEffectiveInterestRate({
  loanAmount,
  arrangementFee,
  annualInterestRate,
  numberOfMonths,
}: EffectiveInterestRateParams): number {
  const netLoanAmount = loanAmount - arrangementFee;

  if (loanAmount <= 0 || netLoanAmount <= 0 || numberOfMonths <= 0) {
    return 0;
  }

  let low = 0;
  let high = MAX_MONTHLY_RATE;
  let monthlyRate = (low + high) / 2;

  for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
    monthlyRate = (low + high) / 2;
    const presentValue = getPresentValue(
      loanAmount,
      annualInterestRate,
      numberOfMonths,
      monthlyRate,
    );

    if (presentValue > netLoanAmount) {
      low = monthlyRate;
    } else {
      high = monthlyRate;
    }

    if (high - low < RATE_INTERVAL_TOLERANCE) {
      break;
    }
  }

  return (Math.pow(1 + monthlyRate, MONTHS_IN_YEAR) - 1) * 100;
}
